import { stringify } from 'qs';

export const getUrlQueryStringPart = (
  params: Record<string, unknown>,
): string => {
  const queryString = Object.keys(params)
    .map((key) => {
      if (params[key] !== undefined) {
        if (Array.isArray(params[key])) {
          return params[key]
            .map((value) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        }
        if (typeof params[key] === 'object') {
          const objectParams = JSON.stringify(params[key]);
          return `${key}=${encodeURIComponent(objectParams)}`;
        }
        return `${key}=${params[key]}`;
      }
      return '';
    })
    .filter((item) => item)
    .join('&');
  return queryString ? `?${queryString}` : '';
};

/**
 * Appends query parameters to a URL, handling existing query strings correctly.
 * Uses proper separator (? or &) based on whether the URL already has query parameters.
 *
 * @param baseUrl - The base URL (may or may not already contain query parameters)
 * @param queryParams - Object containing query parameters to append
 * @param options - Optional configuration for query string serialization
 * @returns URL with query parameters properly appended
 *
 * @example
 * appendQueryParams('/api/applications/my?api-version=1', { Page: 1, PageSize: 10 })
 * // Returns: '/api/applications/my?api-version=1&Page=1&PageSize=10'
 *
 * @example
 * appendQueryParams('/api/applications/my', { apiVersion: 1 })
 * // Returns: '/api/applications/my?apiVersion=1'
 */
export function appendQueryParams(
  baseUrl: string,
  queryParams: Record<string, any>,
  options?: {
    indices?: boolean;
    skipNulls?: boolean;
    arrayFormat?: 'repeat' | 'indices' | 'brackets' | 'comma';
  },
): string {
  if (!queryParams || Object.keys(queryParams).length === 0) {
    return baseUrl;
  }

  const hasQueryParams = baseUrl.includes('?');
  const separator = hasQueryParams ? '&' : '?';

  const queryString = stringify(queryParams, {
    addQueryPrefix: false,
    indices: options?.indices ?? false,
    skipNulls: options?.skipNulls ?? false,
    arrayFormat: options?.arrayFormat ?? 'repeat',
  });

  return `${baseUrl}${separator}${queryString}`;
}
